import React, { useState, useEffect } from 'react';
import { Download, X, Smartphone } from 'lucide-react';

export function PWAInstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if running as standalone PWA
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if already dismissed
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    const dismissedTime = dismissed ? new Date(dismissed) : null;
    const daysSinceDismissed = dismissedTime 
      ? (new Date() - dismissedTime) / (1000 * 60 * 60 * 24) 
      : null;

    // Show prompt if not standalone and not recently dismissed (show again after 7 days)
    if (!standalone && (!dismissed || daysSinceDismissed > 7)) {
      // For Android/Chrome - listen for install prompt
      const handleInstallPrompt = () => {
        setShowPrompt(true);
      };

      window.addEventListener('pwa-installable', handleInstallPrompt);

      // For iOS - show after a delay
      if (iOS) {
        const timer = setTimeout(() => {
          setShowPrompt(true);
        }, 3000);
        return () => clearTimeout(timer);
      }

      return () => {
        window.removeEventListener('pwa-installable', handleInstallPrompt);
      };
    }
  }, []);

  const handleInstall = async () => {
    if (window.deferredPrompt) {
      window.deferredPrompt.prompt();
      const { outcome } = await window.deferredPrompt.userChoice;
      window.deferredPrompt = null;
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('pwa-prompt-dismissed', new Date().toISOString());
    setShowPrompt(false);
  };

  if (!showPrompt || isStandalone) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 z-50 animate-slide-up">
      <div className="bg-white/65 border border-[rgba(0,0,0,0.06)] rounded-2xl shadow-2xl p-4">
        <button 
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1.5 text-[#AEAEB2] hover:text-white rounded-full hover:bg-white/55"
        >
          <X className="w-4 h-4" />
        </button>
        
        <div className="flex items-start gap-3 pr-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-6 h-6 text-white" />
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-white text-sm">Instalar Paliacate OS</h3>
            <p className="text-xs text-[#AEAEB2] mt-0.5">
              {isIOS 
                ? 'Toca el icono de compartir y luego "Agregar a inicio"'
                : 'Accede más rápido desde tu pantalla de inicio'
              }
            </p>
          </div>
        </div>

        {!isIOS && (
          <button
            onClick={handleInstall}
            className="w-full mt-3 py-2.5 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl font-medium text-sm flex items-center justify-center gap-2 hover:shadow-lg transition-shadow"
          >
            <Download className="w-4 h-4" />
            Instalar App
          </button>
        )}

        {isIOS && (
          <div className="mt-3 p-3 bg-white/40 rounded-xl">
            <div className="flex items-center gap-2 text-xs text-[#6E6E73]">
              <span className="text-lg">1.</span>
              <span>Toca</span>
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2l3.5 3.5-1.4 1.4L13 5.8V16h-2V5.8L9.9 6.9 8.5 5.5 12 2zm7 9v9c0 1.1-.9 2-2 2H7c-1.1 0-2-.9-2-2v-9h2v9h10v-9h2z"/>
              </svg>
              <span>en la barra inferior</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-[#6E6E73] mt-2">
              <span className="text-lg">2.</span>
              <span>Selecciona "Agregar a pantalla de inicio"</span>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default PWAInstallPrompt;
